import dayjs from 'dayjs';

const SIGNWELL_API_URL = 'https://www.signwell.com/api/v1';

export class SignWellService {
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
  }

  private getHeaders(): Record<string, string> {
    if (!this.apiKey) {
      throw new Error('SignWell API key is not set');
    }

    return {
      Authorization: `Bearer ${this.apiKey}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Api-Key': this.apiKey,
      'Access-Control-Allow-Origin': '*',
      'X-Frame-Options': 'allow-from *',
    };
  }

  public async createDocument(
    leaseDocumentDetails: any,
    documentTemplateId: string,
  ): Promise<any> {
    try {
      const options = {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          test_mode: true, //TODO: change this to false after testing
          draft: false,
          with_signature_page: false,
          reminders: true,
          apply_signing_order: false,
          embedded_signing: true,
          embedded_signing_notifications: false,
          text_tags: false,
          allow_decline: true,
          allow_reassign: true,
          template_id: documentTemplateId,
          title: 'Rental Agreement',
          recipients: [
            {
              id: 1,
              name: leaseDocumentDetails.tenant_name,
              email: leaseDocumentDetails.tenant_email,
              role: 'Signer',
              placeholder_name: 'renter-tenant',
            },
            {
              id: 2,
              name: leaseDocumentDetails.owner_name,
              email: leaseDocumentDetails.owner_email,
              role: 'Signer',
              placeholder_name: 'landlord-manager',
            },
          ],
          template_fields: [
            //prepopulate some of the fields that we know
            {
              api_id: 'tenant_name_1',
              value: leaseDocumentDetails.tenant_name,
            },
            {
              api_id: 'tenant_name_2', //not required
              value: '',
            },
            {
              api_id: 'landlord_name',
              value: leaseDocumentDetails.owner_name,
            },
            {
              api_id: 'agreement_day',
              value: dayjs().format('DD'),
            },
            {
              api_id: 'agreement_month',
              value: dayjs().format('MMMM'),
            },
            {
              api_id: 'agreement_year',
              value: dayjs().format('YYYY'),
            },
            {
              api_id: 'lease_term',
              value: leaseDocumentDetails.lease_term,
            },
            {
              api_id: 'lease_start_date',
              value: leaseDocumentDetails.lease_start_date,
            },
            {
              api_id: 'lease_end_date',
              value: leaseDocumentDetails.lease_end_date,
            },
            {
              api_id: 'total_monthly_lease',
              value: leaseDocumentDetails.totalCost,
            },
            {
              api_id: 'rental_property_address',
              value: leaseDocumentDetails.address,
            },
            {
              api_id: 'no_of_children',
              value: leaseDocumentDetails.no_of_children ?? 'N/A', //not set as required on template
            },
            {
              api_id: 'cleaning_fee',
              value: leaseDocumentDetails.cleaningFee,
            },
            {
              api_id: 'security_deposit',
              value: leaseDocumentDetails.securityDeposit,
            },
          ],
        }),
      };

      const response = await fetch(
        `${SIGNWELL_API_URL}/document_templates/documents/`,
        options,
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  }

  public async getDocumentURL(documentId: string): Promise<string> {
    try {
      const response = await fetch(`${SIGNWELL_API_URL}/documents/${documentId}`, {
        headers: this.getHeaders(),
      });
      const data = await response.json();
      //   This is returning the signing url for the first recipient(the renter); recepiant[0]-tenant, recepient[1] -is the owner/landlord as we defined in the options above
      //we can return the data here and handle rendering the document to the respective account(renter and property owner)
      return data?.recipients[0]?.embedded_signing_url;
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  }

  //Not really needed - This is to fetch the template document; but it can also be accessed from the web app at signwell
  public async getTemplateDocument(templateId: string): Promise<string> {
    try {
      const response = await fetch(`${SIGNWELL_API_URL}/${templateId}`, {
        headers: this.getHeaders(),
      });
      const data = await response.json();
      return data;
      //   return data.signers[0].embed_url; // Assuming only one signer
    } catch (error) {
      console.error('Error getting document URL:', error);
      throw error;
    }
  }

  public async getDocumentStatus(documentId: string): Promise<any> {
    try {
      const response = await fetch(`${SIGNWELL_API_URL}/documents/${documentId}`, {
        headers: this.getHeaders(),
      });

      const data = await response.json();
      return data.recipients; //returns the recipients array
    } catch (error) {
      console.error('Error fetching document status:', error);
      throw error;
    }
  }
}
